import '@nostr-gives/shared/styles/tokens.css';
import '@nostr-gives/components/styles.css';
import './styles/app.css';

import {
  mountThemeToggle,
  type ThemeValue,
  type ToggleHandle,
  mountTile,
  type TileHandle,
  type TileCompletionProps,
  mountTestModeBanner,
  type TestModeBannerHandle,
  loadEndgameSnapshot,
  utcDateString,
  formatTileRankLabel,
  type EndgameSnapshot,
  iconSvg,
} from '@nostr-gives/components';
import {
  GAME_IDS,
  GAME_CATEGORIES,
  GAME_CATEGORY_LABELS,
  GAME_CATEGORY_ORDER,
  TEST_MODE_BUCKET_MIN,
  TEST_MODE_BUCKET_MS,
} from '@nostr-gives/shared';
import type {
  GameCategory,
  GameId,
  GameInstance,
  GameRegistration,
} from '@nostr-gives/shared';

import { openLoginModal } from './components/login-modal';
import { openShareModalDebug } from './components/share-modal';
import { openHowToPlay } from './components/how-to-play-sheet';
import { loadAuth, clearAuth, initNsecUnlock } from './lib/session';
import { apiJson } from './lib/api';
import { fetchProfile, loadCachedProfile } from './lib/profile';
import { applyTheme, loadTheme, saveTheme, type Theme } from './lib/theme';
import { applyRouteSeo } from './lib/seo';
import {
  syncTestModeFromUrl,
  isTestMode,
  nextBucketAtMs,
} from './lib/test-mode';
import { registry } from './games/registry.gen';
import { mountRpsSpike, type SpikeHandle } from './games/mp-rps-spike';
import { renderHelpAuth } from './pages/help-auth';
import { renderHelpPrivacy } from './pages/help-privacy';
import { renderHelpTerms } from './pages/help-terms';
import { renderLeaderboard } from './pages/leaderboard';
import { renderSpectatorStub, parseSpectatePath } from './pages/spectate';

// Resolve dev/test-mode from URL before anything reads the flag.
syncTestModeFromUrl();

// Decrypt the stored nsec into memory (device-key mode, transparent). Kicked
// off at boot so the in-memory cache is populated well before any game reaches
// its publish step. Non-blocking: header/auth chrome reads loadAuth(), not the
// nsec, so nothing here gates the first render.
void initNsecUnlock();

const app = document.getElementById('app');
if (!app) throw new Error('No #app root element.');

let currentGame: GameInstance | null = null;
let authButtonHost: HTMLDivElement | null = null;
let leaderboardRankHost: HTMLDivElement | null = null;
let themeHandle: ToggleHandle | null = null;
const tileHandles: TileHandle[] = [];

// Tracks the last pubkey we requested the daily-rank for so we don't spam
// /api/leaderboard/me on every re-render. Cleared when the route or auth
// state changes.
let lastRankFetchKey: string | null = null;
const loadedRegistrations = new Map<string, GameRegistration>();

const debugMode = new URLSearchParams(window.location.search).get('debug') === '1';

// ---------- theme ----------

function applyThemeValue(value: Theme): void {
  if (value === 'system') {
    applyTheme('system');
    return;
  }
  applyTheme(value);
}

const systemMql = window.matchMedia('(prefers-color-scheme: dark)');
function onSystemThemeChange(): void {
  if (loadTheme() === 'system') {
    // Reapply so the cross-fade transition triggers and any dependent code
    // reading data-theme=null gets a chance to re-evaluate.
    applyTheme('system');
  }
}
systemMql.addEventListener('change', onSystemThemeChange);

// ---------- auth control ----------

// Tracks the last pubkey we kicked off a background profile fetch for,
// so re-entering renderAuthControl() (which happens on every modal close
// and on PFP-load resolve) doesn't spam the relays. Cleared on sign-out
// implicitly by the next sign-in writing a fresh pubkey.
let pfpFetchInFlightFor: string | null = null;

function pubkeyStub(pubkey: string): string {
  if (pubkey.startsWith('stub_')) return pubkey.slice(5, 7);
  if (pubkey.startsWith('anon_')) return pubkey.slice(5, 7);
  return pubkey.slice(0, 2);
}

// User-menu dropdown anchored to the header avatar. Mirrors the Surf
// pattern (packages/frontend/src/components/header.ts in nostr-surf):
// one-tap Log out, outside-click closes, no confirm step. The dropdown
// is appended to the avatar trigger so absolute positioning anchors
// against it without measurement.
function closeUserDropdown(): void {
  const existing = document.getElementById('user-dropdown');
  if (existing) existing.remove();
  const trigger = document.getElementById('user-info');
  if (trigger) trigger.setAttribute('aria-expanded', 'false');
}

function performLogout(): void {
  // clearAuth() removes BOTH `gives.session` (the auth record incl.
  // pubkey + signerType + sessionToken) AND `gives.nsec` (any persisted
  // nsec1 from the nsec-mode signer). No separate NIP-46 connect-string
  // is stored client-side; the bunker URL lives only in the modal's
  // transient state during the connect handshake. Worker game-session
  // tokens under `gives.session.<gameId>` are intentionally left in
  // place so a re-login in the same day rehydrates progress.
  clearAuth();
  closeUserDropdown();
  // Force a full reload to drop any in-memory pubkey-derived state
  // (rank fetch keys, profile fetch in-flight markers, opened sheets).
  // Surf does `window.location.hash = '#/'` because its router is
  // hash-based; ours is too, but a soft hash change won't repaint the
  // header or evict in-memory caches. A reload is cheap and safe.
  window.location.hash = '#/';
  window.location.reload();
}

function toggleUserDropdown(trigger: HTMLElement): void {
  const existing = document.getElementById('user-dropdown');
  if (existing) {
    closeUserDropdown();
    return;
  }

  const dropdown = document.createElement('div');
  dropdown.id = 'user-dropdown';
  dropdown.className = 'user-dropdown';
  dropdown.setAttribute('role', 'menu');

  const logoutBtn = document.createElement('button');
  logoutBtn.type = 'button';
  logoutBtn.id = 'logout-btn';
  logoutBtn.className = 'user-dropdown-item';
  logoutBtn.setAttribute('role', 'menuitem');
  // Lucide log-out glyph, sized to match the existing 18px header icons.
  logoutBtn.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>' +
    '<span>Log out</span>';
  logoutBtn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    performLogout();
  });
  dropdown.appendChild(logoutBtn);

  trigger.appendChild(dropdown);
  trigger.setAttribute('aria-expanded', 'true');

  const close = (ev: MouseEvent): void => {
    if (!dropdown.contains(ev.target as Node)) {
      closeUserDropdown();
      document.removeEventListener('click', close);
    }
  };
  // Defer the outside-click listener by a tick so this click doesn't
  // immediately close the menu we just opened.
  setTimeout(() => document.addEventListener('click', close), 10);
}

function renderAuthControl(): void {
  if (!authButtonHost) return;
  authButtonHost.innerHTML = '';
  const auth = loadAuth();
  if (auth) {
    const cached = loadCachedProfile(auth.pubkey);
    const label = cached?.display_name || cached?.name || auth.pubkey.slice(0, 8);

    // Wrap the avatar in a clickable trigger so tapping it opens the
    // user dropdown (Log out, etc). Mirrors nostr.surf's #user-info
    // pattern: the avatar IS the button. The dropdown is appended to
    // this trigger and positioned absolutely against it.
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = 'user-info';
    trigger.className = 'user-info';
    trigger.setAttribute('aria-label', 'Open user menu');
    trigger.setAttribute('aria-haspopup', 'menu');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.dataset.authorHex = auth.pubkey;

    if (cached?.picture) {
      // Real profile picture path. Img is sized to match the .avatar
      // dimensions (32x32) and the .avatar class still applies its
      // border-radius so we keep one source of truth for the rounded
      // shape. On image error we replace the img with the digit-stub
      // span so a broken URL never paints a busted img tag.
      const img = document.createElement('img');
      img.className = 'avatar';
      img.src = cached.picture;
      img.alt = label;
      img.title = auth.pubkey;
      img.width = 32;
      img.height = 32;
      img.referrerPolicy = 'no-referrer';
      img.decoding = 'async';
      img.loading = 'lazy';
      img.onerror = (): void => {
        const fallback = document.createElement('span');
        fallback.className = 'avatar';
        fallback.title = auth.pubkey;
        fallback.textContent = pubkeyStub(auth.pubkey);
        img.replaceWith(fallback);
      };
      trigger.appendChild(img);
    } else {
      const avatar = document.createElement('span');
      avatar.className = 'avatar';
      avatar.title = auth.pubkey;
      avatar.textContent = pubkeyStub(auth.pubkey);
      trigger.appendChild(avatar);
    }

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleUserDropdown(trigger);
    });

    authButtonHost.appendChild(trigger);

    // Fire-and-forget refresh. We skip when:
    //  - we already have a fresh cached PFP (the cache TTL is the
    //    refresh window), OR
    //  - a fetch is already in-flight for THIS pubkey, OR
    //  - the pubkey is anon/stub (no real kind 0 to fetch).
    const isReal = !auth.pubkey.startsWith('anon_') && !auth.pubkey.startsWith('stub_');
    const needsFetch = isReal && !cached?.picture && pfpFetchInFlightFor !== auth.pubkey;
    if (needsFetch) {
      pfpFetchInFlightFor = auth.pubkey;
      void fetchProfile(auth.pubkey)
        .then((profile) => {
          if (profile?.picture) renderAuthControl();
        })
        .finally(() => {
          if (pfpFetchInFlightFor === auth.pubkey) pfpFetchInFlightFor = null;
        });
    }
    return;
  }
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sign-in-btn';
  btn.textContent = 'Sign in';
  btn.addEventListener('click', () => {
    openLoginModal(() => {
      renderHeaderRight();
    });
  });
  authButtonHost.appendChild(btn);
}

// ---------- leaderboard rank chip (top-right) ----------

/**
 * Top-right chrome ordering (left-to-right):
 *   {leaderboard icon} {#rank} {medal emoji 1/2/3} {Sign in OR avatar}
 *
 * Anon users see ONLY the leaderboard icon (no rank, no medal) - it still
 * navigates to /#/leaderboard so unauthenticated browsing of the board is
 * one tap away. Signed-in users get a live rank chip backed by
 * /api/leaderboard/me?utcDate=<today>&pubkey=<hex>. Medal emoji glyphs are
 * iconography (not copy) - Bowz approved the unicode shortcut over inline
 * SVG to keep this commit small; swap to SVG if a screenshot looks ugly.
 */

interface MeRankResponse {
  dailyRank: number | null;
  dailyTotal?: number;
  daily?: { rank: number | null };
}

function medalForRank(rank: number): string | null {
  if (rank === 1) return '\u{1F947}';
  if (rank === 2) return '\u{1F948}';
  if (rank === 3) return '\u{1F949}';
  return null;
}

function renderRankChip(host: HTMLElement, dailyRank: number | null): void {
  host.innerHTML = '';
  const a = document.createElement('a');
  a.href = '#/leaderboard';
  a.className = dailyRank !== null ? 'gv-header-rank-chip' : 'gv-header-leaderboard-link';
  a.setAttribute('aria-label', dailyRank !== null
    ? `Open leaderboard - today's rank #${dailyRank}`
    : 'Open leaderboard');
  a.appendChild(iconSvg('trophy', 18));
  if (dailyRank !== null) {
    const rankText = document.createElement('span');
    rankText.className = 'gv-header-rank-num';
    rankText.textContent = `#${dailyRank}`;
    a.appendChild(rankText);
    const medal = medalForRank(dailyRank);
    if (medal) {
      const m = document.createElement('span');
      m.className = 'gv-header-rank-medal';
      m.textContent = medal;
      m.setAttribute('aria-hidden', 'true');
      a.appendChild(m);
    }
  }
  host.appendChild(a);
}

async function fetchDailyRank(pubkey: string): Promise<number | null> {
  try {
    const path = `/api/leaderboard/me?utcDate=${utcDateString()}&pubkey=${encodeURIComponent(pubkey)}`;
    const { status, data } = await apiJson<MeRankResponse>(path);
    if (status !== 200 || !data) return null;
    if (typeof data.dailyRank === 'number') return data.dailyRank;
    if (data.daily && typeof data.daily.rank === 'number') return data.daily.rank;
    return null;
  } catch {
    return null;
  }
}

function isRealPubkey(pubkey: string): boolean {
  return !pubkey.startsWith('anon_') && !pubkey.startsWith('stub_');
}

function renderHeaderRight(): void {
  // Always (re)render the auth slot - cheap, and keeps the PFP fresh when
  // the cached profile resolves out-of-band.
  renderAuthControl();

  if (!leaderboardRankHost) return;
  const auth = loadAuth();

  // Anonymous or non-real pubkey: trophy icon only, links to leaderboard.
  if (!auth || !auth.signerType || auth.signerType === 'anon' || !isRealPubkey(auth.pubkey)) {
    lastRankFetchKey = null;
    renderRankChip(leaderboardRankHost, null);
    return;
  }

  // Signed-in: paint the icon-only state immediately, then resolve the
  // rank async so first-paint never blocks on a network round-trip.
  renderRankChip(leaderboardRankHost, null);

  // De-dupe rapid re-entries (e.g. modal close + visibilitychange firing
  // in the same tick). Key on pubkey + UTC day so a new day forces a
  // fresh fetch.
  const fetchKey = `${auth.pubkey}|${utcDateString()}`;
  if (lastRankFetchKey === fetchKey) {
    // We already fetched for this slot recently - but the icon-only state
    // we just painted dropped the rank. Refetch only if the host is empty
    // of a rank number, which it now is.
  }
  lastRankFetchKey = fetchKey;

  const hostAtFetchTime = leaderboardRankHost;
  void fetchDailyRank(auth.pubkey).then((rank) => {
    // Guard against teardown / route change while the fetch was in flight.
    if (leaderboardRankHost !== hostAtFetchTime) return;
    const liveAuth = loadAuth();
    if (!liveAuth || liveAuth.pubkey !== auth.pubkey) return;
    renderRankChip(hostAtFetchTime, rank);
  });
}

// ---------- shell ----------

function renderShell(): void {
  app!.innerHTML = '';

  const header = document.createElement('header');
  header.className = 'app-header';

  // Wordmark group: "nostr.gives" + brain/bulb/puzzle mark to its right.
  // The mark is the canonical site logo from public/nostr-gives-icon-logo.png.
  const wordmark = document.createElement('a');
  wordmark.href = '#/';
  wordmark.className = 'wordmark';
  wordmark.style.textDecoration = 'none';
  wordmark.style.color = 'inherit';
  wordmark.style.display = 'inline-flex';
  wordmark.style.alignItems = 'center';
  wordmark.style.gap = '10px';
  const wordmarkText = document.createElement('span');
  wordmarkText.textContent = 'nostr.gives';
  const wordmarkMark = document.createElement('img');
  wordmarkMark.src = '/nostr-gives-icon-logo.png';
  wordmarkMark.alt = '';
  wordmarkMark.width = 32;
  wordmarkMark.height = 32;
  wordmarkMark.style.display = 'block';
  wordmark.append(wordmarkText, wordmarkMark);

  const right = document.createElement('div');
  right.className = 'header-right';

  // New top-right chrome (PM 2026-06-12): the theme toggle moved to the
  // footer. The right side now hosts {leaderboard rank chip} +
  // {sign-in OR avatar}, in that order.
  leaderboardRankHost = document.createElement('div');
  leaderboardRankHost.className = 'gv-header-rank-host';
  right.appendChild(leaderboardRankHost);

  authButtonHost = document.createElement('div');
  authButtonHost.className = 'gv-header-auth-host';
  right.appendChild(authButtonHost);

  // Apply the persisted theme on first paint - the toggle itself is
  // mounted into the footer further down.
  const initialTheme = loadTheme();
  applyThemeValue(initialTheme);

  header.append(wordmark, right);
  app!.appendChild(header);

  const main = document.createElement('main');
  main.id = 'main-view';
  app!.appendChild(main);

  const footer = document.createElement('footer');
  footer.className = 'app-footer';

  // Theme toggle row (PM 2026-06-12): moved out of the top-right chrome so
  // the header can show {trophy + rank + medal} + {sign-in/avatar} only.
  // Sits ABOVE the existing tagline + link row.
  const themeRow = document.createElement('div');
  themeRow.className = 'app-footer-theme';
  themeHandle = mountThemeToggle(themeRow, {
    value: initialTheme,
    onChange: (next: ThemeValue): void => {
      saveTheme(next);
      applyThemeValue(next);
      themeHandle?.update({
        value: next,
        onChange: (later: ThemeValue): void => {
          saveTheme(later);
          applyThemeValue(later);
        },
      });
    },
  });
  footer.appendChild(themeRow);

  const tag = document.createElement('div');
  tag.textContent = 'nostr.gives - no signup required - reset 00:00 UTC';
  const links = document.createElement('div');
  links.className = 'links';
  const linkSpecs: Array<[string, string]> = [
    ['#/', 'About'],
    ['#/help/auth', 'Auth'],
    ['#/help/privacy', 'Privacy'],
    ['#/help/terms', 'Terms'],
    ['https://github.com/NostrGives/nostr-gives', 'GitHub'],
  ];
  for (const [href, label] of linkSpecs) {
    const a = document.createElement('a');
    a.href = href;
    if (href.startsWith('http')) a.rel = 'noopener';
    a.textContent = label;
    links.appendChild(a);
  }
  footer.append(tag, links);
  app!.appendChild(footer);

  renderHeaderRight();

  if (debugMode) renderDebugBar();
}

function renderDebugBar(): void {
  const bar = document.createElement('div');
  bar.className = 'debug-bar';

  const shareBtn = document.createElement('button');
  shareBtn.type = 'button';
  shareBtn.textContent = 'Test share';
  shareBtn.addEventListener('click', () => openShareModalDebug());
  bar.appendChild(shareBtn);

  const htpBtn = document.createElement('button');
  htpBtn.type = 'button';
  htpBtn.textContent = 'How to play';
  htpBtn.addEventListener('click', () =>
    openHowToPlay({
      gameName: 'WORD5',
      bodyHtml:
        '<p>WORD5 stub - wiring proof only, will be deleted in Phase 2.0.</p>' +
        '<p>Guess the 5-letter word in six tries. Green = right letter, right spot. Orange = right letter, wrong spot.</p>',
    }),
  );
  bar.appendChild(htpBtn);

  app!.appendChild(bar);
}

// ---------- home (tile grid) ----------

// PM 2026-06-15 (Phase 21): some game ids cannot be used as folder names
// because JS module / export identifiers cannot start with a digit.
// `2048` is the canonical game id (in GAME_IDS, in the worker, in
// `/api/puzzle/2048`) but the frontend folder must be `game-2048`. This
// map is the single source of truth for those aliases.
const GAME_ID_TO_FOLDER: Record<string, string> = {
  '2048': 'game-2048',
};

function folderForGameId(gameId: string): string {
  return GAME_ID_TO_FOLDER[gameId] ?? gameId;
}

async function ensureRegistration(gameId: string): Promise<GameRegistration | null> {
  const folderId = folderForGameId(gameId);
  const cached = loadedRegistrations.get(folderId);
  if (cached) return cached;
  const loader = registry[folderId];
  if (!loader) return null;
  const mod = await loader();
  // Each game module exports either a named export matching its camelCase id
  // or a default export.
  const named: GameRegistration | undefined = pickRegistration(mod, folderId);
  if (!named) return null;
  loadedRegistrations.set(folderId, named);
  return named;
}

function pickRegistration(
  mod: { default?: GameRegistration } & Record<string, GameRegistration>,
  folderId: string,
): GameRegistration | undefined {
  if (mod.default) return mod.default;
  const exportName = toExportName(folderId);
  return mod[exportName];
}

function toExportName(folderId: string): string {
  return folderId.replace(/-([a-z0-9])/g, (_, ch: string) => (ch as string).toUpperCase());
}

/**
 * PM 2026-06-11 (tile completion badges): read the latest snapshot for the
 * given game/UTC-date and project it into the tile's completion overlay.
 *
 * In TEST MODE we additionally require the snapshot to fall inside the
 * CURRENT 10-minute rotation bucket - otherwise puzzle #N's snapshot would
 * leak through and the badge would falsely persist after the worker rotated
 * to puzzle #N+1 (see Bug 1 / `end-game-controller.rehydrate` API change).
 *
 * In PROD a single puzzle covers the entire UTC day, so the latest snapshot
 * with matching `utcDate` is always the current one.
 */
function readTileCompletion(gameId: string): TileCompletionProps | null {
  const utc = utcDateString();
  const snap: EndgameSnapshot | null = loadEndgameSnapshot(gameId, utc);
  if (!snap) return null;
  if (snap.utcDate !== utc) return null;

  if (isTestMode()) {
    // Snapshot must live inside the current bucket. Anything older was
    // recorded against a now-rotated puzzle and should NOT light up the tile.
    const now = Date.now();
    const bucketStart = now - (now % TEST_MODE_BUCKET_MS);
    if (snap.savedAt < bucketStart) return null;
  }

  const primary = snap.stats.primary;
  const time = snap.stats.time ?? '';
  // Phase 19 (PM 2026-06-11): project the snapshot's dailyRank into a
  // tile-friendly label. `loadAuth()` decides whether the player is logged
  // in for ranking purposes (anon sessions show "Sign in to rank").
  const auth = loadAuth();
  const isLoggedIn = Boolean(auth && auth.signerType && auth.signerType !== 'anon');
  return {
    outcome: snap.outcome,
    primary,
    time,
    rank: formatTileRankLabel(snap.dailyRank ?? null, isLoggedIn),
  };
}

async function renderHome(): Promise<void> {
  const main = document.getElementById('main-view');
  if (!main) return;
  main.innerHTML = '';

  // Drop stale tile handles.
  for (const h of tileHandles) h.destroy();
  tileHandles.length = 0;

  const hero = document.createElement('section');
  hero.className = 'hero';
  hero.innerHTML =
    '<h1>Daily puzzles for Nostr.</h1>' +
    '<p class="subhead">Five minutes, then back to your day.</p>';
  main.appendChild(hero);

  // Tile grid container (PM 2026-06-12): the body now hosts ONE section per
  // category. Each category section has an H2 heading and its own grid of
  // tiles. The legacy `.tile-grid` class is preserved as the outer container
  // so existing max-width / centering wins still apply; the per-category
  // sub-grids use `.gv-tile-category-grid`.
  const grid = document.createElement('section');
  grid.className = 'tile-grid tile-grid-categorised';
  grid.setAttribute('aria-label', 'Available games');
  main.appendChild(grid);

  // Render tiles in canonical GAME_IDS order. WORD5 (the dev stub) is not in
  // GAME_IDS; surface it only with `?debug=1` so prod tile grid matches spec.
  const ids: string[] = [...GAME_IDS];
  if (debugMode) ids.push('word5');

  const regs = await Promise.all(
    ids.map((id) => ensureRegistration(id).then((r) => [id, r] as const)),
  );

  // Resolve a category for every id we are about to render. WORD5 (debug
  // stub) and any future unknown id bucket to 'other' so the grid never
  // silently drops a tile.
  function categoryFor(id: string): GameCategory {
    return GAME_CATEGORIES[id as GameId] ?? 'other';
  }

  // Bucket the resolved registrations by category, preserving the canonical
  // GAME_IDS order WITHIN each bucket. WORD5 (debug) lands in 'other'.
  const byCategory = new Map<GameCategory, Array<readonly [string, GameRegistration | null]>>();
  for (const entry of regs) {
    const cat = categoryFor(entry[0]);
    const list = byCategory.get(cat) ?? [];
    list.push(entry);
    byCategory.set(cat, list);
  }

  for (const category of GAME_CATEGORY_ORDER) {
    const entries = byCategory.get(category);
    if (!entries || entries.length === 0) continue;

    const heading = document.createElement('h2');
    heading.className = 'gv-tile-category-heading';
    heading.textContent = GAME_CATEGORY_LABELS[category];
    grid.appendChild(heading);

    const subGrid = document.createElement('div');
    subGrid.className = 'gv-tile-category-grid';
    subGrid.setAttribute('data-category', category);
    grid.appendChild(subGrid);

    for (const [id, reg] of entries) {
      if (!reg) {
        // No module - render a disabled placeholder so the grid shape stays stable.
        const handle = mountTile(subGrid, {
          gameId: id,
          name: prettyName(id),
          tagline: 'Coming soon',
          tileImagePath: `/tiles/${id}.png?v=4`,
          enabled: false,
          onClick: (): void => {},
        });
        tileHandles.push(handle);
        continue;
      }

      const enabled = reg.enabled;
      // Dormant tiles get no completion badge even if a snapshot exists.
      const completion = enabled ? (readTileCompletion(reg.id) ?? undefined) : undefined;
      const handle = mountTile(subGrid, {
        gameId: reg.id,
        name: reg.name,
        tagline: reg.tagline,
        tileImagePath: `/tiles/${reg.id}.png?v=4`,
        enabled,
        ...(completion ? { completion } : {}),
        onClick: (): void => {
          if (!enabled) return;
          window.location.hash = '#/' + reg.id;
        },
      });
      tileHandles.push(handle);
    }
  }
}

// PM 2026-06-11: refresh tile completion overlays when the player finishes
// a game in another tab (storage event) or returns to the homepage tab
// (visibilitychange). Both are cheap because `renderHome` short-circuits
// when no route applies.
function refreshHomeIfMounted(): void {
  const hash = window.location.hash || '#/';
  const path = hash.slice(1);
  if (path === '/' || path === '') {
    void renderHome();
  }
}
window.addEventListener('storage', (ev) => {
  if (!ev.key || !ev.key.startsWith('gives.endgame.')) return;
  refreshHomeIfMounted();
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') refreshHomeIfMounted();
});

function prettyName(id: string): string {
  return id
    .split('-')
    .map((s) => (s.length === 0 ? s : (s[0] ?? '').toUpperCase() + s.slice(1)))
    .join(' ');
}

// ---------- game route ----------

async function renderGame(gameId: string): Promise<void> {
  const main = document.getElementById('main-view');
  if (!main) return;
  main.innerHTML = '';

  const reg = await ensureRegistration(gameId);
  if (!reg) {
    window.location.hash = '#/';
    return;
  }

  const auth = loadAuth();
  if (currentGame) {
    currentGame.unmount();
    currentGame = null;
  }
  currentGame = await reg.mount(main, {
    user: auth ? { pubkey: auth.pubkey, npub: '' } : null,
    sessionToken: auth?.sessionToken ?? '',
    publishShare: async (): Promise<void> => {
      // Real publisher wires in per-game share modals (Phase 2+).
    },
    trackStreak: (): void => {
      // Real streak tracker hooks in per-game (Phase 2+).
    },
  });
}

// ---------- help routes ----------

function renderHelpRoute(path: string): void {
  const main = document.getElementById('main-view');
  if (!main) return;
  if (path === '/help/auth') renderHelpAuth(main);
  else if (path === '/help/privacy') renderHelpPrivacy(main);
  else if (path === '/help/terms') renderHelpTerms(main);
  else {
    main.innerHTML = '';
    window.location.hash = '#/';
  }
}

// ---------- router ----------

// Phase 17 throwaway: mounted at `#/mp-rps-spike` and `#/mp-rps-spike/<roomId>`.
// Held outside `currentGame` because it doesn't go through the games registry.
let currentSpike: SpikeHandle | null = null;

function teardownSpike(): void {
  if (currentSpike) {
    currentSpike.unmount();
    currentSpike = null;
  }
}

function route(): void {
  // Keep document title + meta + canonical synced to the current hash route.
  // Crawlers don't see this (they get the static pre-rendered HTML emitted by
  // `scripts/generate-static-landings.mjs`) - this is for runtime previews
  // when a human shares a deep link.
  applyRouteSeo();
  if (currentGame) {
    currentGame.unmount();
    currentGame = null;
  }
  teardownSpike();
  const hash = window.location.hash || '#/';
  const path = hash.slice(1);
  if (path === '/' || path === '') {
    void renderHome();
    return;
  }
  if (path.startsWith('/help/')) {
    renderHelpRoute(path);
    return;
  }
  // Phase 19: leaderboard hash route. Accept `/leaderboard` and any
  // `/leaderboard?...` query suffix so deep-links from the end-game pill
  // (`/#/leaderboard?focus=me`) can pass through.
  if (path === '/leaderboard' || path.startsWith('/leaderboard?') || path.startsWith('/leaderboard&')) {
    const main = document.getElementById('main-view');
    if (main) renderLeaderboard(main, hash);
    return;
  }
  // Phase 17.5.1 spectator stub. Accepts both `/spectate/:gameId/:roomId`
  // and the legacy short form `/spectate/:roomId`. Renders a "coming soon"
  // card; full spectator implementation is queued.
  if (path === '/spectate' || path.startsWith('/spectate/')) {
    const main = document.getElementById('main-view');
    if (main) {
      const parsed = parseSpectatePath(path);
      renderSpectatorStub(main, {
        gameId: parsed?.gameId ?? null,
        roomId: parsed?.roomId ?? null,
      });
    }
    return;
  }
  // Phase 17 vertical-slice harness. Accepts `/mp-rps-spike` (host lobby)
  // and `/mp-rps-spike/<roomId>` (guest landing). Throwaway - deleted
  // after slice acceptance per the runbook.
  if (path === '/mp-rps-spike' || path.startsWith('/mp-rps-spike/')) {
    const main = document.getElementById('main-view');
    if (main) {
      const segments = path.split('/').filter(Boolean);
      const roomId = segments[1] ?? null;
      main.innerHTML = '';
      currentSpike = mountRpsSpike(main, roomId);
    }
    return;
  }
  if (path.startsWith('/')) {
    const gameId = path.slice(1);
    void renderGame(gameId);
    return;
  }
  void renderHome();
}

renderShell();
route();
window.addEventListener('hashchange', route);

// Test-mode countdown banner (PM 2026-06-10 rotation fix). Mounted on
// document.body so it stays visible across `#/` route changes. The
// banner self-hides via `data-active="0"` when test mode is OFF.
let testModeBannerHandle: TestModeBannerHandle | null = null;
{
  const banner = mountTestModeBanner(document.body, {
    active: isTestMode(),
    nextRotationAtMs: nextBucketAtMs(),
    bucketMinutes: TEST_MODE_BUCKET_MIN,
  });
  testModeBannerHandle = banner;
  // Recompute the next-bucket boundary every second; the banner's own
  // ticker only updates the countdown label, it does not re-derive the
  // boundary. When the boundary crosses, push a new `nextRotationAtMs`.
  let lastNext = nextBucketAtMs();
  setInterval(() => {
    const cur = nextBucketAtMs();
    const active = isTestMode();
    if (cur !== lastNext || active !== (banner as { _active?: boolean })._active) {
      banner.update({
        active,
        nextRotationAtMs: cur,
        bucketMinutes: TEST_MODE_BUCKET_MIN,
      });
      lastNext = cur;
    }
  }, 1000);
}
// Reference kept so future teardown (if we ever need it) can reach the
// handle. Currently the banner lives for the lifetime of the page.
void testModeBannerHandle;

// Cross-component bus: a game's end-game overlay (anonymous) asks the
// shell to open the Login Modal in place. We re-render the entire
// header-right chrome on resolve so the rank chip reflects the new
// session alongside the auth control.
window.addEventListener('gives:open-login', () => {
  openLoginModal(() => {
    renderHeaderRight();
  });
});

// Refresh the rank chip when the tab regains focus (rank moves while
// the user was away) and once a minute while visible. The minute
// interval is a floor - /api/leaderboard/me is CDN-cached for 10s so
// this stays cheap. Clearing `lastRankFetchKey` forces a real refetch.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    lastRankFetchKey = null;
    renderHeaderRight();
  }
});
setInterval(() => {
  if (document.visibilityState !== 'visible') return;
  lastRankFetchKey = null;
  renderHeaderRight();
}, 60_000);
